import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Header from "@/components/Header";
import RecordButton from "@/components/RecordButton";
import EmotionDisplay from "@/components/EmotionDisplay";
import TranscriptDisplay from "@/components/TranscriptDisplay";
import ApiKeyDialog from "@/components/ApiKeyDialog";
import { toast } from "@/components/ui/use-toast";
import { audioService } from "@/services/audioService";
import { emotionService } from "@/services/emotionService";
import { Emotion } from "@/services/emotionService";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, InfoIcon } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

const Index = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [emotions, setEmotions] = useState<Record<string, number>>({});
  const [speechRate, setSpeechRate] = useState(0);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasApiKey, setHasApiKey] = useState(false);
  const [showKeyDialog, setShowKeyDialog] = useState(false);
  const [offlineMode, setOfflineMode] = useState(false);

  // Check if API key is set on component mount
  useEffect(() => {
    const hasKey = emotionService.hasApiKey();
    setHasApiKey(hasKey);
    
    // If no API key is set, suggest setting one up
    if (!hasKey) {
      setShowKeyDialog(true);
      toast({
        title: "API Key Required",
        description: "Please set your Hugging Face API key to use emotion detection",
        duration: 5000,
      });
    }
  }, []);

  // Toggle offline mode
  const handleOfflineModeToggle = (enabled: boolean) => {
    setOfflineMode(enabled);
    emotionService.setOfflineMode(enabled);
  };

  const toggleRecording = async () => {
    if (!isRecording) {
      try {
        // Start recording
        await audioService.startRecording();
        setIsRecording(true);
        setAnalysisComplete(false);
        toast({
          title: "Recording started",
          description: "Speak clearly into your microphone.",
        });
      } catch (error) {
        console.error("Failed to start recording:", error);
      }
    } else {
      try {
        // Stop recording and process audio
        setIsRecording(false);
        setIsProcessing(true);
        
        // Get the recorded audio
        const { audioBlob, audioDuration } = await audioService.stopRecording();
        
        try {
          // Process with Hugging Face model or simulation
          const analysisResult = await emotionService.analyzeAudio(audioBlob);
          
          // Set the emotions and transcript
          setTranscript(analysisResult.transcript);
          setEmotions(analysisResult.emotions);
          
          // Calculate speech rate
          const calculatedSpeechRate = audioService.calculateSpeechRate(
            analysisResult.transcript, 
            audioDuration
          );
          setSpeechRate(calculatedSpeechRate);
          
          // Mark analysis as complete
          setAnalysisComplete(true);
          toast({
            title: "Analysis complete",
            description: "Your speech has been analyzed.",
          });
        } catch (error) {
          console.error("Error with emotion analysis:", error);
          toast({
            title: "Analysis Failed",
            description: "There was an error analyzing your speech",
            variant: "destructive",
          });
        }
        
        setIsProcessing(false);
      } catch (error) {
        console.error("Failed to process recording:", error);
        setIsProcessing(false);
        toast({
          title: "Processing Error",
          description: "There was an error processing your recording.",
          variant: "destructive",
        });
      }
    }
  };

  const handleApiKeySet = () => {
    setHasApiKey(emotionService.hasApiKey());
    setShowKeyDialog(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-slate-800">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        <Card className="w-full max-w-4xl mx-auto shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-2xl">Speech Emotion Analysis</CardTitle>
              <CardDescription>
                Record your speech to analyze emotion and speech rate in real-time
              </CardDescription>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center space-x-2">
                <Switch 
                  id="offline-mode" 
                  checked={offlineMode} 
                  onCheckedChange={handleOfflineModeToggle} 
                />
                <Label htmlFor="offline-mode">Offline Mode</Label>
              </div>
              <ApiKeyDialog onApiKeySet={handleApiKeySet} initialOpen={showKeyDialog} />
            </div>
          </CardHeader>
          
          <CardContent>
            <div className="flex flex-col items-center gap-6">
              {!hasApiKey && !offlineMode && (
                <Alert className="mb-4">
                  <InfoIcon className="h-4 w-4" />
                  <AlertTitle>API Key Recommended</AlertTitle>
                  <AlertDescription>
                    For the best experience, set your Hugging Face API key or enable offline mode.
                  </AlertDescription>
                </Alert>
              )}

              {offlineMode && (
                <Alert className="mb-4">
                  <InfoIcon className="h-4 w-4" />
                  <AlertTitle>Offline Mode Active</AlertTitle>
                  <AlertDescription>
                    Using simulated emotion analysis based on audio characteristics.
                  </AlertDescription>
                </Alert>
              )}
              
              <RecordButton 
                isRecording={isRecording} 
                onClick={toggleRecording} 
                disabled={isProcessing}
              />
              
              {isProcessing && (
                <div className="text-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500 mx-auto"></div>
                  <p className="mt-2">Processing your speech...</p>
                </div>
              )}
              
              {analysisComplete && (
                <Tabs defaultValue="emotions" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="emotions">Emotion Analysis</TabsTrigger>
                    <TabsTrigger value="transcript">Transcript</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="emotions" className="space-y-4">
                    <EmotionDisplay emotions={emotions} speechRate={speechRate} />
                  </TabsContent>
                  
                  <TabsContent value="transcript">
                    <TranscriptDisplay transcript={transcript} />
                  </TabsContent>
                </Tabs>
              )}
            </div>
          </CardContent>
          
          <CardFooter className="flex justify-between flex-wrap text-sm text-gray-500 dark:text-gray-400">
            <p>
              Model: {offlineMode ? "Simulated Analysis" : "Hugging Face - Speech Emotion Recognition with OpenAI Whisper"}
            </p>
            <p>Speech Rate: {analysisComplete ? `${speechRate} words/min` : "N/A"}</p>
          </CardFooter>
        </Card>
      </main>
    </div>
  );
};

export default Index;
